import { QrContentData } from './types';

// Escapes the reserved characters in the WIFI: payload grammar (\ ; , : ").
function escapeWifi(value: string): string {
  return value.replace(/([\\;,":])/g, '\\$1');
}

// Escapes a vCard 3.0 text value (RFC 2426 §4): backslash, comma, semicolon and
// line breaks (encoded as \n). A lone \r must be caught too — buyer-typed field
// values end up in these lines, and an unescaped line break would let them inject
// arbitrary vCard properties into the scanned contact.
function escapeVcard(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/([,;])/g, '\\$1')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * Turns typed QR content into the encoded string that gets rendered into the QR image.
 * Pure and framework-agnostic — this is the heart of the QR "library".
 */
export default function buildQrPayload(content: QrContentData): string {
  switch (content.type) {
    case 'url': {
      const url = content.url.trim();
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    }

    case 'text':
      return content.text;

    case 'email': {
      const params: string[] = [];
      if (content.subject) {
        params.push(`subject=${encodeURIComponent(content.subject)}`);
      }
      if (content.body) {
        params.push(`body=${encodeURIComponent(content.body)}`);
      }
      const query = params.length ? `?${params.join('&')}` : '';
      // Percent-encode the address (keeping a readable @) so reserved characters in a
      // buyer-typed value can't break the URI or inject extra query parameters.
      const to = encodeURIComponent(content.to.trim()).replace(/%40/g, '@');
      return `mailto:${to}${query}`;
    }

    case 'phone':
      return `tel:${content.number.replace(/\s/g, '')}`;

    case 'sms':
      return content.message ? `SMSTO:${content.number}:${content.message}` : `SMSTO:${content.number}`;

    case 'wifi': {
      const encryption = content.encryption ?? 'WPA';
      const parts = [`T:${encryption}`, `S:${escapeWifi(content.ssid)}`];
      if (encryption !== 'nopass' && content.password) {
        parts.push(`P:${escapeWifi(content.password)}`);
      }
      if (content.hidden) {
        parts.push('H:true');
      }
      return `WIFI:${parts.join(';')};;`;
    }

    case 'vcard': {
      const first = content.firstName?.trim() ?? '';
      const last = content.lastName?.trim() ?? '';
      // FN is mandatory with content (RFC 2426) and iOS handles an empty FN badly.
      // When no name is mapped, fall back to the strongest identifying field.
      const displayName =
        `${first} ${last}`.trim() ||
        content.organization?.trim() ||
        content.email?.trim() ||
        content.phones?.[0]?.number.trim() ||
        content.phone?.trim() ||
        content.url?.trim() ||
        content.title?.trim() ||
        '';
      const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `N:${escapeVcard(last)};${escapeVcard(first)};;;`,
        `FN:${escapeVcard(displayName)}`,
      ];
      if (content.organization) {
        lines.push(`ORG:${escapeVcard(content.organization)}`);
      }
      if (content.title) {
        lines.push(`TITLE:${escapeVcard(content.title)}`);
      }
      const phones = content.phones ?? (content.phone ? [{ number: content.phone }] : []);
      phones.forEach((entry) => {
        const number = escapeVcard(entry.number.trim());
        // TYPE is a vCard parameter token: strip anything that could break the line.
        const telType = (entry.telType || 'CELL').toUpperCase().replace(/[^A-Z-]/g, '') || 'CELL';
        if (number) {
          lines.push(`TEL;TYPE=${telType}:${number}`);
        }
      });
      if (content.email) {
        lines.push(`EMAIL;TYPE=INTERNET:${escapeVcard(content.email)}`);
      }
      if (content.url) {
        lines.push(`URL:${escapeVcard(content.url)}`);
      }
      if (content.linkedin) {
        // X-SOCIALPROFILE is what iOS renders as a proper profile row; the plain
        // URL;TYPE line is the fallback most Android parsers pick up.
        lines.push(`X-SOCIALPROFILE;TYPE=linkedin:${escapeVcard(content.linkedin)}`);
        lines.push(`URL;TYPE=LinkedIn:${escapeVcard(content.linkedin)}`);
      }
      if (content.address) {
        // Whole address in the ADR "street" component.
        lines.push(`ADR;TYPE=WORK:;;${escapeVcard(content.address)};;;;`);
      }
      lines.push('END:VCARD');
      // The vCard spec (RFC 2426 §2.4.2) mandates CRLF line delimiters — iOS in
      // particular is strict about it.
      return lines.join('\r\n');
    }

    default:
      return '';
  }
}
